import {
  Component,
  effect,
  inject,
  input,
  InputSignal,
  signal,
} from '@angular/core';
import {EControlActions, IControlDataChanged, IControlObject} from 'frontier/nucleus';
import {
  IInvoiceTemplate,
  IInvoiceTemplates,
  ITemplateConfigurationDto,
  ITemplateModificationDto,
} from './template.interface';
import {FormsModule} from '@angular/forms';
import {Observable, of, Unsubscribable} from 'rxjs';
import {TemplatesFormComponent} from './templates-form.component.ts/templates-form.component';
import {
  ConfirmationDialogComponent, DialogService,
  EntitySelectorComponent,
  ISelectFormOption,
  SelectElementComponent,
} from 'frontier/browserkit';
import {MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup} from '@angular/material/button-toggle';
import {AsyncPipe, NgIf} from '@angular/common';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {MatButton} from '@angular/material/button';
import {SplitterModule} from 'primeng/splitter';
import AppControlGuids from '../../../api/ControlGuids';
import {DunningTemplateConfigurationControlService, TDBObject} from '../../../api/generated';
import {filter, tap} from 'rxjs/operators';
import {ISelectableEntity} from 'frontier/browserkit/src/lib/dialogs/entity-selector/selectable-entity.interface';
import {ILanguageSelectFormOption, ILanguageSelectObject, TemplatesEditorComponent} from './templates-editor.component';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {TemplatesEditorMapper} from './templates-editor.mapper';

@Component({
  selector: 'app-dunnings-editor',
  templateUrl: './templates-editor.component.html',
  standalone: true,
  imports: [
    NgIf,
    AsyncPipe,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatSlideToggle,
    FormsModule,
    TemplatesFormComponent,
    MatButton,
    SplitterModule,
    SelectElementComponent,
    MatIcon,
    MatTooltip,
  ],
  styleUrl: './dunnings-editor.component.scss',
})
export class DunningsEditorComponent extends TemplatesEditorComponent {
  parent: InputSignal<IControlObject> = input.required();

  private _dialogService: DialogService = inject(DialogService);
  private _dunningTemplateConfigurationControlService: DunningTemplateConfigurationControlService
    = inject(DunningTemplateConfigurationControlService);

  override GUID: string = AppControlGuids.TDunningTemplateConfigurationControl;

  private _configInitialized = false;

  constructor() {
    super();

    // Override isInvoice and isCancellationConfig for dunnings
    this.isInvoice = false;
    this.isCancellationConfig = signal<boolean>(false);

    effect((): void => {
      const invoiceData: IInvoiceTemplates = this.data();
      this.selectedInvoice.set(invoiceData?.data[0]);
    }, {allowSignalWrites: true})

    effect((): void => {
      const parent: IControlObject = this.parent();
      if (parent) {
        this.patchFilter({parent});
      }
    }, {allowSignalWrites: true});

    effect((): void => {
      const instanceId: string = this.apiInstance()?.instanceid;
      if (instanceId && !this._configInitialized) {
        this._configInitialized = true;
        this._dunningTemplateConfigurationControlService.dunningTemplateConfigurationControlGetConfiguration({
          TemplateConfigObj: null,
          Language: null,
        }).subscribe((configDto: ITemplateConfigurationDto): void => {
          this.standardConfigDto.set(configDto);
          this.standardForm.set(TemplatesEditorMapper.toModel(configDto));
          if (configDto) {
            this._templateConfigurationControlService.templateConfigurationControlGetDataFields()
              .subscribe((res: any): void => {
                this.dataFields.set(res);
              })
          }
        });
      }
    }, {allowSignalWrites: true});

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged): boolean => {
          return new Map([
            [AppControlGuids.TDunningConfigView, new Set([EControlActions.changeLine, EControlActions.create])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap((): Unsubscribable => this.changeAndFetchInstance())).subscribe()
    );
  }

  protected override getDataFields(): void {
    this._dunningTemplateConfigurationControlService.dunningTemplateConfigurationControlGetDataFields(
      {TemplateConfigObj: this.selectedInvoice() as TDBObject ?? undefined}
    )
      .subscribe((res: any): void => {
        this.dataFields.set(res);
      });
  }

  protected override requestLanguageRepos(selectedInvoice: IInvoiceTemplate | 1): void {
    this._dunningTemplateConfigurationControlService
      .dunningTemplateConfigurationControlGetRepository({TemplateConfigObj: selectedInvoice as TDBObject || null})
      .subscribe((res: ILanguageSelectObject[]): void => {
        this.updateLanguageRepos(res.map((item: ILanguageSelectObject): ILanguageSelectFormOption => {
          return {
            ...item,
            value: item,
          } as ILanguageSelectFormOption;
        }));
      });
  }

  protected override fetchInvoiceConfiguration<T extends ITemplateConfigurationDto | ITemplateModificationDto>(
    isCancellationConfig: boolean,
    language: TDBObject,
    templateConfig?: IInvoiceTemplate,
  ): Observable<T> {
    if (!this.apiInstance()?.instanceid) return of(null);
    if (!templateConfig || !language) return of(null);
    if (!this.configuredLanguages().map((l: ILanguageSelectFormOption): string => l.signature)
      .includes(language.signature)) return of(null);
    this.refreshPreview(isCancellationConfig, language, templateConfig);
    return this._dunningTemplateConfigurationControlService.dunningTemplateConfigurationControlGetConfiguration({
      TemplateConfigObj: templateConfig,
      Language: language,
    })
  }

  protected override refreshPreview(
    isCancellationConfig: boolean,
    language: TDBObject,
    templateConfig: IInvoiceTemplate,
  ): void {
    if (!templateConfig || !language) return;
    if (!this.configuredLanguages().map((l: ILanguageSelectFormOption): string => l.signature)
      .includes(language.signature)) return;
    this._dunningTemplateConfigurationControlService.dunningTemplateConfigurationControlGeneratePdfPreview({
      TemplateConfigObj: templateConfig ? templateConfig : null,
      Language: language,
    }).subscribe({
      next: (res: Blob): void => {
        const url: string = URL.createObjectURL(res);
        this.pdfSrc$.next(this.sanitizer.bypassSecurityTrustResourceUrl(url));
      },
      error: (): void => {
        this._feedbackService.setError('Beim Generieren der PDF ist ein unbekannter Fehler aufgetreten.');
        this.pdfSrc$.next(null);
      }
    })
  }

  protected override performSaveConfiguration(
    configData: ITemplateConfigurationDto | ITemplateModificationDto,
    selectedInvoice: 1 | IInvoiceTemplate
  ): Observable<boolean> {
    return this._dunningTemplateConfigurationControlService.dunningTemplateConfigurationControlSetConfiguration({
      Language: this.languageFormControl.value,
      TemplateConfigObj: this.isStandardFormSelected() ? null : selectedInvoice as IInvoiceTemplate,
      ConfigData: configData,
    });
  }

  override onSelectedInvoiceChange(event: MatButtonToggleChange): void {
    if (typeof event.value === 'number' ? event.value === this.selectedInvoice()
      : event.value.signature === (this.selectedInvoice() as IInvoiceTemplate).signature) return;

    const currentFormRef: TemplatesFormComponent = this.displayedFormRef();

    // Ask to confirm if there are unsaved changes
    if (currentFormRef?.form?.dirty) {
      this.confirmDiscardChanges().subscribe((result: boolean): void => {
        if (result) {
          this.languageReposLoaded.set(false);
          this.selectedInvoice.set(event.value);
        } else {
          event.source.buttonToggleGroup.value = this.selectedInvoice();
        }
      })
    } else {
      if (event.value === '+') {
        return;
      }

      this.languageReposLoaded.set(false);
      this.selectedInvoice.set(event.value);
    }
  }

  protected override createLanguageConfiguration(): void {
    this.dialog.open(EntitySelectorComponent, {
      data: {
        title: 'Neue Sprache konfigurieren',
        entities: this.unconfiguredLanguages().map((val: ISelectFormOption): ISelectableEntity => {
          return {displayName: val.name, id: val.name};
        }),
        showCustomCheckBox: !!this.configuredLanguages()
          .find((el: ILanguageSelectFormOption): boolean => el.usedefault),
        checkBoxText: 'Werte von Standardsprache übernehmen',
      }
    }).afterClosed().subscribe((res: any): void => {
      if (res) {
        const newLanguage: ISelectFormOption = this.unconfiguredLanguages()
          .find((el: ISelectFormOption): boolean => el.name === res.result[0]);
        this._dunningTemplateConfigurationControlService.dunningTemplateConfigurationControlCreateConfig({
          InstanceId: this.instanceId(),
          Language: newLanguage,
          TemplateConfigObj: this.selectedInvoice() as TDBObject,
          usedefault: res.checkBox || false,
        }).subscribe((result: boolean): void => {
          if (result) {
            if (newLanguage.value.usedefault) {
              this.defaultLanguageConfigured.emit();
            }
            this.requestLanguageRepos(this.selectedInvoice());
            this.languageFormControl.setValue(newLanguage);
          }
        });
      }
    });
  }

  protected override addDunningLevel(): void {
    this._dunningTemplateConfigurationControlService.dunningTemplateConfigurationControlNewDunningLevel(
      {TemplateConfigObj: this.parent()}
    )
      .subscribe(((res: boolean): void => {
        if (res) {
          this.changeAndFetchInstance();
        }
      }));
  }

  protected override deleteDunningLevel(): void {
    this._dialogService.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Wollen Sie die letzte Mahnstufe wirklich löschen?',
        description: 'Die Löschung kann nicht rückgängig gemacht werden. Dies beinhaltet die Konfiguration für alle ' +
          'Sprachen, in denen die Mahnstufe konfiguriert ist.',
        confirmButtonText: 'Ja',
        cancelButtonText: 'Nein',
        securityPhrase: 'Mahnung',
      },
    }).afterClosed()
      .pipe(
        tap((res: any): void => {
          if (res) {
            this._dunningTemplateConfigurationControlService.dunningTemplateConfigurationControlDeleteDunninLevel({
              TemplateConfigObj: this.data().data[this.data().data.length - 1] as TDBObject,
            }).subscribe(((res: boolean): void => {
              if (res) {
                this.changeAndFetchInstance();
              }
            }));
          }
        }),
      ).subscribe();
  }

  protected override deleteConfig(): void {
    this._dunningTemplateConfigurationControlService.dunningTemplateConfigurationControlDeleteConfig({
      TemplateConfigObj: this.selectedInvoice() as TDBObject,
      Language: this.languageSignal(),
    }).subscribe((res: boolean): void => {
      this.handleSuccessfulDelete(res);
    });
  }
}
